import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronRight, Plus, Search, Users, UserRound, Gift, Coins, CreditCard, Bell, FilePlus2, FileText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";
import { norm } from "@/lib/format";

const GROUP_ICON: Record<string, React.ComponentType<any>> = {
  "Registro y cuenta": UserRound, Puntos: Coins, Canjes: Gift, Planes: CreditCard, "Avisos para el equipo": Users,
};

export const editorPath = (key: string, template?: string, from?: string) =>
  `/admin/notificaciones/editor/${key}${template ? `?plantilla=${template}` : ""}${template && from ? `&desde=${from}` : ""}`;

const START_OPTIONS = [
  { value: "blanco" as const, icon: FilePlus2, title: "En blanco", text: "Un lienzo vacío para diseñarla desde cero. Las variables y bloques se insertan con «/»." },
  { value: "sugerido" as const, icon: FileText, title: "Con el texto sugerido", text: "Parte del texto de Armony para esta notificación y ajústalo a tu gusto." },
];

// Lista de notificaciones automáticas: qué evento las dispara, qué plantilla usan y por qué canal salen.
// Los canales se encienden y apagan aquí mismo; el texto se edita en el editor.
export default function AdminNotificationTemplates() {
  usePageTitle("Plantillas · Notificaciones automáticas");
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [newEvent, setNewEvent] = useState("");
  const [newStart, setNewStart] = useState<"blanco" | "sugerido">("blanco");

  const load = () => {
    setFailed(false);
    return api.get("/admin/notifications/events").then((r) => setData(r.data)).catch(() => setFailed(true));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (ev: any, field: "email_enabled" | "portal_enabled", value: boolean) => {
    if (ev.audience === "equipo" && field === "email_enabled" && value && !ev.settings.recipients) {
      toast.info("Primero escribe los correos del equipo que deben recibirlo.");
      navigate(editorPath(ev.key) + "#envio");
      return;
    }
    setSaving(`${ev.key}:${field}`);
    try {
      const { data: saved } = await api.put(`/admin/notifications/events/${ev.key}`, { ...ev.settings, [field]: value });
      setData((d: any) => ({ ...d, events: d.events.map((e: any) => (e.key === saved.key ? saved : e)) }));
      toast.success(`${field === "email_enabled" ? "Correo" : "Aviso en el portal"} ${value ? "activado" : "desactivado"}: ${ev.name}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(""); }
  };

  const groups = useMemo(() => {
    const out: Record<string, any[]> = {};
    (data?.events || [])
      .filter((e: any) => !q || norm(`${e.name} ${e.when} ${e.subject}`).includes(norm(q)))
      .forEach((e: any) => { (out[e.group] = out[e.group] || []).push(e); });
    return out;
  }, [data, q]);

  if (failed) return <LoadError what="las notificaciones" onRetry={load} />;
  if (!data) return <PageLoader />;

  return (
    <div className="space-y-6" data-testid="admin-notification-templates">
      {data.config?.problems?.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3" role="status">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-slate-800">
            <p className="font-medium">Los correos todavía no salen; los avisos en el portal sí.</p>
            <ul className="list-disc pl-5 text-slate-700">{data.config.problems.map((p: string) => <li key={p}>{p}</li>)}</ul>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <Input value={q} onChange={(e: any) => setQ(e.target.value)} placeholder="Buscar notificación" aria-label="Buscar notificación"
            className="pl-9" autoComplete="off" data-testid="notification-search" />
        </div>
        <Button onClick={() => { setNewEvent(""); setNewStart("blanco"); setNewOpen(true); }} className="gold-gradient-bg text-white font-semibold rounded-full ml-auto" data-testid="notification-new">
          <Plus className="w-4 h-4 mr-1.5" aria-hidden="true" /> Nueva plantilla
        </Button>
      </div>

      {Object.keys(groups).length === 0 && <EmptyState icon={Bell}>No hay notificaciones que coincidan con “{q}”.</EmptyState>}

      {Object.entries(groups).map(([group, events]) => {
        const Icon = GROUP_ICON[group] || Bell;
        return (
          <section key={group} className="card-obsidian overflow-hidden" aria-labelledby={`grp-${group}`}>
            <h2 id={`grp-${group}`} className="flex items-center gap-2 px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider font-semibold text-slate-600">
              <Icon className="w-4 h-4 text-navy" aria-hidden="true" /> {group}
            </h2>
            <div className="hidden md:grid grid-cols-[1fr_190px_84px_84px_32px] gap-4 px-5 py-2 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <span>Se envía cuando…</span><span>Plantilla en uso</span><span className="text-center">Correo</span><span className="text-center">Portal</span><span />
            </div>
            <ul className="divide-y divide-slate-100">
              {events.map((ev: any) => (
                <li key={ev.key} className="group grid grid-cols-[1fr_auto] md:grid-cols-[1fr_190px_84px_84px_32px] gap-x-4 gap-y-2 items-center px-5 py-4 hover:bg-slate-50/70" data-testid="notification-event">
                  <button type="button" onClick={() => navigate(editorPath(ev.key))} className="text-left min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded" data-testid={`notification-edit-${ev.key}`}>
                    <p className="font-semibold text-slate-900 group-hover:text-navy">{ev.name}</p>
                    <p className="text-sm text-slate-600">{ev.when}</p>
                    <p className="text-xs text-slate-500 mt-1 truncate">Asunto: {ev.subject}</p>
                  </button>
                  <div className="col-start-1 md:col-start-auto">
                    {ev.active_template
                      ? <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-navy/10 text-navy">{ev.active_template.name}</span>
                      : <span className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">Texto sugerido</span>}
                    {ev.templates > 0 && <span className="block text-[11px] text-slate-500 mt-1">{ev.templates} {ev.templates === 1 ? "plantilla guardada" : "plantillas guardadas"}</span>}
                    {ev.audience === "equipo" && <span className="block text-[11px] text-slate-500 mt-1">Para el equipo</span>}
                  </div>
                  <div className="flex md:justify-center items-center gap-2">
                    <Switch checked={ev.settings.email_enabled} disabled={saving === `${ev.key}:email_enabled`}
                      onCheckedChange={(v: boolean) => toggle(ev, "email_enabled", v)} aria-label={`Correo: ${ev.name}`} data-testid={`notification-email-${ev.key}`} />
                    <span className="md:hidden text-xs text-slate-600">Correo</span>
                  </div>
                  <div className="flex md:justify-center items-center gap-2">
                    {ev.has_portal ? <>
                      <Switch checked={ev.settings.portal_enabled} disabled={saving === `${ev.key}:portal_enabled`}
                        onCheckedChange={(v: boolean) => toggle(ev, "portal_enabled", v)} aria-label={`Aviso en el portal: ${ev.name}`} />
                      <span className="md:hidden text-xs text-slate-600">Portal</span>
                    </> : <span className="text-xs text-slate-400" title="Este aviso es solo por correo">—</span>}
                  </div>
                  <ChevronRight className="hidden md:block w-4 h-4 text-slate-400 group-hover:text-navy" aria-hidden="true" />
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900" data-testid="notification-new-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Nueva plantilla</DialogTitle>
            <DialogDescription className="text-slate-600">Elige cuándo se envía y cómo quieres empezar. Cada notificación tiene sus propias variables (nombre, premio, puntos…).</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="new-event" className="text-sm text-slate-700">Se envía cuando…</Label>
            <Select value={newEvent} onValueChange={setNewEvent}>
              <SelectTrigger id="new-event" className="mt-1.5" data-testid="notification-new-event"><SelectValue placeholder="Elige la notificación" /></SelectTrigger>
              <SelectContent>
                {data.events.map((e: any) => <SelectItem key={e.key} value={e.key}>{e.group} · {e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <fieldset>
            <legend className="text-sm text-slate-700 mb-2">¿Cómo quieres empezar?</legend>
            <div className="grid sm:grid-cols-2 gap-3">
              {START_OPTIONS.map((o) => (
                <label key={o.value} className={`flex gap-3 rounded-xl border-2 p-3 cursor-pointer transition-colors ${newStart === o.value ? "border-navy bg-navy/5" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`notification-new-start-${o.value}`}>
                  <input type="radio" name="new-start" value={o.value} checked={newStart === o.value} onChange={() => setNewStart(o.value)} className="sr-only" />
                  <o.icon className="w-5 h-5 text-navy shrink-0 mt-0.5" aria-hidden="true" />
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{o.title}</span>
                    <span className="block text-xs text-slate-600 mt-0.5">{o.text}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button disabled={!newEvent} onClick={() => navigate(editorPath(newEvent, "nueva", newStart))} className="gold-gradient-bg text-white font-semibold rounded-full" data-testid="notification-new-continue">
              Crear plantilla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
